var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { format } from "date-fns";
import { ConsentDataResourceScope } from "healthex-common/consentscopes.dtos";
import { computed } from "mobx";
import { FormModel } from "./FormModel";
import { NamedRoutes } from "./NamedRoutes";
import { getObservableT } from "../Infra/i18next";
import { MobileBottomNavigationButtonType } from "./PatientResearchDashboardUIManager";
import { SharingSection } from "./PatientStudiesListViewModel";
const t = getObservableT("patients");
const SMART_LINK_DATA_TYPE_TO_CONSENT_SCOPE = {
    CONDITIONS: ConsentDataResourceScope.DIAGNOSES,
    MEDICATIONS: ConsentDataResourceScope.MEDICATIONS,
    LABS: ConsentDataResourceScope.LABS,
    VITALS: ConsentDataResourceScope.CLINICAL_VITALS,
    CLINICAL_VISITS: ConsentDataResourceScope.VISITS,
    ALLERGIES: ConsentDataResourceScope.ALLERGIES,
    PROCEDURES: ConsentDataResourceScope.PROCEDURES,
    IMMUNIZATIONS: ConsentDataResourceScope.IMMUNIZATIONS,
    CLINICAL_NOTES: ConsentDataResourceScope.CLINICAL_NOTES,
};
export const SMART_LINK_DATA_TYPE_KEYS = [
    "CONDITIONS",
    "MEDICATIONS",
    "LABS",
    "VITALS",
    "CLINICAL_VISITS",
    "ALLERGIES",
    "PROCEDURES",
    "IMMUNIZATIONS",
    "CLINICAL_NOTES",
];
function defaultLinkName() {
    return `Created on ${format(new Date(), "MMM dd, yyyy")}`;
}
export class PatientCreateSmartLinkViewModel {
    constructor(props) {
        this.props = props;
        this.formModel = new FormModel({
            linkName: defaultLinkName(),
            linkExpires: "ONE_TIME",
            whatToInclude: "ALL",
            selectedDataTypes: [...SMART_LINK_DATA_TYPE_KEYS],
        }, ["linkName"]);
        this.formModel.onInputUpdate = (update) => {
            if ("linkName" in update)
                this.formModel.clearError("linkName");
            this.formModel.clearFormError();
        };
    }
    formInputToCreateSHLDto() {
        const input = this.formModel.input;
        const fullRecord = input.whatToInclude === "ALL";
        const consentDataResourceScopes = fullRecord
            ? []
            : [
                ConsentDataResourceScope.DEMOGRAPHICS,
                ConsentDataResourceScope.CONTACT_INFO,
                ...input.selectedDataTypes.map((k) => SMART_LINK_DATA_TYPE_TO_CONSENT_SCOPE[k]),
            ];
        return {
            label: input.linkName.trim(),
            isOneTimeUse: input.linkExpires === "ONE_TIME",
            fullRecordWithoutRestrictedData: fullRecord,
            consentDataResourceScopes,
        };
    }
    get canSubmit() {
        const { linkName } = this.formModel.input;
        return Boolean(linkName === null || linkName === void 0 ? void 0 : linkName.trim()) && !this.formModel.hasErrors();
    }
    setLinkExpires(value) {
        this.formModel.updateInput({ linkExpires: value });
    }
    setWhatToInclude(value) {
        if (value === "SELECTED") {
            this.formModel.updateInput({ whatToInclude: value, selectedDataTypes: [...SMART_LINK_DATA_TYPE_KEYS] });
        }
        else {
            this.formModel.updateInput({ whatToInclude: value });
        }
    }
    clearSelectedDataTypes() {
        this.formModel.updateInput({ selectedDataTypes: [] });
    }
    selectAllDataTypes() {
        this.formModel.updateInput({ selectedDataTypes: [...SMART_LINK_DATA_TYPE_KEYS] });
    }
    get allDataTypesSelected() {
        return this.formModel.input.selectedDataTypes.length === SMART_LINK_DATA_TYPE_KEYS.length;
    }
    toggleDataType(key) {
        const current = this.formModel.input.selectedDataTypes;
        const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
        this.formModel.updateInput({ selectedDataTypes: next });
    }
    isDataTypeSelected(key) {
        return this.formModel.input.selectedDataTypes.includes(key);
    }
    async submit() {
        var _a, _b, _c, _d, _e, _f;
        this.formModel.clearErrors();
        if (!((_a = this.formModel.input.linkName) === null || _a === void 0 ? void 0 : _a.trim())) {
            this.formModel.addError("linkName", "Enter a name for this link. This name is only visible to you.");
            (_c = (_b = this.props).onScrollToLinkName) === null || _c === void 0 ? void 0 : _c.call(_b);
            return;
        }
        if (!this.canSubmit)
            return;
        if (this.props.onCreateShareLink) {
            await this.props.onCreateShareLink(this.formModel.input);
            return;
        }
        const container = this.props.container;
        const patientId = (_e = (_d = container === null || container === void 0 ? void 0 : container.authManager) === null || _d === void 0 ? void 0 : _d.id) !== null && _e !== void 0 ? _e : null;
        const shlService = container === null || container === void 0 ? void 0 : container.shlService;
        if (!patientId || !shlService) {
            this.formModel.addFormError(t("Something went wrong, please try again later."));
            return;
        }
        try {
            const dto = this.formInputToCreateSHLDto();
            const shl = await shlService.createSHL(patientId, dto);
            (_f = container.routingUIManager) === null || _f === void 0 ? void 0 : _f.navigateTo(NamedRoutes.PATIENT_SMART_LINK_DETAILS, { shlId: shl.id });
        }
        catch {
            this.formModel.addFormError(t("Something went wrong, please try again later."));
        }
    }
    cancel() {
        var _a;
        if (this.props.onCancel) {
            this.props.onCancel();
        }
        else {
            (_a = this.props.container) === null || _a === void 0 ? void 0 : _a.patientResearchDashboardUIManager.goToPage(MobileBottomNavigationButtonType.RESEARCH, SharingSection.SHARED_LINKS);
        }
    }
}
__decorate([
    computed
], PatientCreateSmartLinkViewModel.prototype, "canSubmit", null);
__decorate([
    computed
], PatientCreateSmartLinkViewModel.prototype, "allDataTypesSelected", null);
